import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import { ChevronDown, LayoutGrid, List } from 'lucide-react'
import { Section, CountryFlagIcon } from '../../ui'
import EntityProfileLink from '../../watchlist/EntityProfileLink'
import WatchlistControl from '../../WatchlistControl'
import {
  affiliationPeriod,
  collectUniqueRoleLabels,
  CompanyRoleEntry,
  filterAffiliationEntries,
  formatActiveSince,
  formatAffiliationPeriod,
  getAffiliationSortOptions,
  readStoredAffiliationSort,
  readStoredAffiliationView,
  roleLabel,
  sortAffiliationEntries,
  storeAffiliationSort,
  storeAffiliationView,
  type AffiliationListVariant,
  type AffiliationSortKey,
  type AffiliationViewMode,
} from './personAffiliations'
import {
  companyCountryFlagCode,
  formatCompanyForm,
  formatStatusLabel,
  isDissolvedStatus,
  isDistressedStatus,
} from '../../../utils/format'
import { PersonAffiliation } from '../../../types'

interface PersonAffiliationsViewProps {
  entries: CompanyRoleEntry[]
  sectionTitle: string
  sectionDescription?: string
  variant?: AffiliationListVariant
  /** Hide top divider when stacked directly under another block (e.g. overview strip). */
  borderTop?: boolean
}

function listAriaLabel(variant: AffiliationListVariant): string {
  return variant === 'active' ? 'Nuværende virksomhedsroller' : 'Tidligere roller'
}

function emptyMessage(variant: AffiliationListVariant, filtersActive: boolean): string {
  if (filtersActive) return 'Ingen tilknytninger matcher filteret.'
  return variant === 'active'
    ? 'Ingen nuværende virksomhedsroller.'
    : 'Ingen tidligere roller.'
}

const PersonAffiliationsView: React.FC<PersonAffiliationsViewProps> = ({
  entries,
  sectionTitle,
  sectionDescription,
  variant = 'active',
  borderTop = true,
}) => {
  const [viewMode, setViewMode] = useState<AffiliationViewMode>(() => readStoredAffiliationView())
  const [sortKey, setSortKey] = useState<AffiliationSortKey>(() => readStoredAffiliationSort(variant))
  const [textQuery, setTextQuery] = useState('')
  /** Empty set = all roles included (default). Non-empty = filter to selected roles. */
  const [filteredRoles, setFilteredRoles] = useState<Set<string>>(() => new Set())

  const roleOptions = useMemo(() => collectUniqueRoleLabels(entries), [entries])
  const filteredEntries = useMemo(
    () =>
      sortAffiliationEntries(
        filterAffiliationEntries(entries, textQuery, filteredRoles),
        sortKey,
      ),
    [entries, textQuery, filteredRoles, sortKey],
  )

  const handleViewChange = (mode: AffiliationViewMode) => {
    setViewMode(mode)
    storeAffiliationView(mode)
  }

  const handleSortChange = (key: AffiliationSortKey) => {
    setSortKey(key)
    storeAffiliationSort(key, variant)
  }

  const toggleRoleFilter = (label: string) => {
    setFilteredRoles((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      return next
    })
  }

  const filtersActive = textQuery.trim().length > 0 || filteredRoles.size > 0

  return (
    <Section
      title={sectionTitle}
      description={sectionDescription}
      compact
      borderTop={borderTop}
      trailing={
        <AffiliationToolbar
          textQuery={textQuery}
          onTextQueryChange={setTextQuery}
          roleOptions={roleOptions}
          filteredRoles={filteredRoles}
          onToggleRoleFilter={toggleRoleFilter}
          sortKey={sortKey}
          onSortChange={handleSortChange}
          variant={variant}
          viewMode={viewMode}
          onViewModeChange={handleViewChange}
        />
      }
    >
      {filteredEntries.length === 0 ? (
        <p className="text-sm text-ink-500">{emptyMessage(variant, filtersActive)}</p>
      ) : viewMode === 'cards' ? (
        <AffiliationCardGrid entries={filteredEntries} variant={variant} />
      ) : (
        <AffiliationEntryList entries={filteredEntries} variant={variant} />
      )}
    </Section>
  )
}

interface AffiliationToolbarProps {
  textQuery: string
  onTextQueryChange: (value: string) => void
  roleOptions: string[]
  filteredRoles: ReadonlySet<string>
  onToggleRoleFilter: (label: string) => void
  sortKey: AffiliationSortKey
  onSortChange: (key: AffiliationSortKey) => void
  variant: AffiliationListVariant
  viewMode: AffiliationViewMode
  onViewModeChange: (mode: AffiliationViewMode) => void
}

/** Shared outer height incl. 1px border — matches ViewModeToggle frame. */
const TOOLBAR_CONTROL =
  'h-7 box-border border border-rule rounded-sm text-xs leading-none'

const AffiliationToolbar: React.FC<AffiliationToolbarProps> = ({
  textQuery,
  onTextQueryChange,
  roleOptions,
  filteredRoles,
  onToggleRoleFilter,
  sortKey,
  onSortChange,
  variant,
  viewMode,
  onViewModeChange,
}) => (
  <div className="flex items-center gap-1.5">
    <label className="block h-7 shrink-0">
      <span className="sr-only">Filtrér tilknytninger</span>
      <input
        type="search"
        value={textQuery}
        onChange={(e) => onTextQueryChange(e.target.value)}
        placeholder="Søg…"
        className={`${TOOLBAR_CONTROL} w-[12rem] sm:w-[14.4rem] appearance-none px-2 bg-paper focus:outline-none focus:ring-1 focus:ring-signal placeholder:text-ink-400`}
      />
    </label>
    {roleOptions.length > 0 && (
      <RoleFilterDropdown
        roleOptions={roleOptions}
        filteredRoles={filteredRoles}
        onToggleRoleFilter={onToggleRoleFilter}
      />
    )}
    <SortDropdown sortKey={sortKey} onSortChange={onSortChange} variant={variant} />
    <ViewModeToggle viewMode={viewMode} onChange={onViewModeChange} />
  </div>
)

interface RoleFilterDropdownProps {
  roleOptions: string[]
  filteredRoles: ReadonlySet<string>
  onToggleRoleFilter: (label: string) => void
}

const RoleFilterDropdown: React.FC<RoleFilterDropdownProps> = ({
  roleOptions,
  filteredRoles,
  onToggleRoleFilter,
}) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const triggerLabel =
    filteredRoles.size > 0 ? `Roller (${filteredRoles.size})` : 'Roller'

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Filtrér roller"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex ${TOOLBAR_CONTROL} items-center gap-1 px-2 text-ink-600 hover:text-ink hover:border-ink-300 bg-paper transition-colors`}
      >
        <span>{triggerLabel}</span>
        <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Filtrér roller"
          className="absolute right-0 top-full z-30 mt-1 min-w-[12rem] max-h-[min(16rem,calc(100vh-1.5rem))] overflow-y-auto bg-paper border border-rule shadow-lg rounded-sm py-1"
        >
          {roleOptions.map((label) => {
            const selected = filteredRoles.has(label)
            return (
              <button
                key={label}
                type="button"
                role="menuitemcheckbox"
                aria-checked={selected}
                onClick={() => onToggleRoleFilter(label)}
                className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-ink hover:bg-paper-200 transition-colors"
              >
                <span aria-hidden className="w-3 shrink-0 text-[10px] text-ink-500">
                  {selected ? '✓' : ''}
                </span>
                <span className={selected ? 'font-semibold' : ''}>{label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

interface SortDropdownProps {
  sortKey: AffiliationSortKey
  onSortChange: (key: AffiliationSortKey) => void
  variant: AffiliationListVariant
}

const SortDropdown: React.FC<SortDropdownProps> = ({ sortKey, onSortChange, variant }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const sortOptions = getAffiliationSortOptions(variant)
  const activeLabel =
    sortOptions.find((o) => o.value === sortKey)?.label ?? 'Sorter'

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Sorter tilknytninger"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex ${TOOLBAR_CONTROL} items-center gap-1 px-2 text-ink-600 hover:text-ink hover:border-ink-300 bg-paper transition-colors`}
      >
        <span>{activeLabel}</span>
        <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Sorter tilknytninger"
          className="absolute right-0 top-full z-30 mt-1 min-w-[11rem] max-h-[min(16rem,calc(100vh-1.5rem))] overflow-y-auto bg-paper border border-rule shadow-lg rounded-sm py-1"
        >
          {sortOptions.map((option) => {
            const selected = sortKey === option.value
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  onSortChange(option.value)
                  setOpen(false)
                }}
                className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-ink hover:bg-paper-200 transition-colors"
              >
                <span aria-hidden className="w-3 shrink-0 text-[10px] text-ink-500">
                  {selected ? '✓' : ''}
                </span>
                <span className={selected ? 'font-semibold' : ''}>{option.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

interface ViewModeToggleProps {
  viewMode: AffiliationViewMode
  onChange: (mode: AffiliationViewMode) => void
}

const ViewModeToggle: React.FC<ViewModeToggleProps> = ({ viewMode, onChange }) => (
  <div
    role="group"
    aria-label="Visning"
    className={`inline-flex shrink-0 overflow-hidden ${TOOLBAR_CONTROL}`}
  >
    <button
      type="button"
      aria-pressed={viewMode === 'list'}
      aria-label="Listevisning"
      title="Listevisning"
      onClick={() => onChange('list')}
      className={`inline-flex h-full w-7 items-center justify-center transition-colors ${
        viewMode === 'list' ? 'bg-ink text-paper' : 'bg-paper text-ink-500 hover:text-ink'
      }`}
    >
      <List className="h-3.5 w-3.5" aria-hidden />
    </button>
    <button
      type="button"
      aria-pressed={viewMode === 'cards'}
      aria-label="Kortvisning"
      title="Kortvisning"
      onClick={() => onChange('cards')}
      className={`inline-flex h-full w-7 items-center justify-center border-l border-rule transition-colors ${
        viewMode === 'cards' ? 'bg-ink text-paper' : 'bg-paper text-ink-500 hover:text-ink'
      }`}
    >
      <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
    </button>
  </div>
)

const CompactRoleTag: React.FC<{ label: string }> = ({ label }) => (
  <span className="inline-flex px-1.5 py-0.5 text-[10px] leading-tight rounded-sm bg-paper-200 text-ink-600 border border-rule">
    {label}
  </span>
)

const CompanyStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  if (!isDissolvedStatus(status) && !isDistressedStatus(status)) return null
  const dissolved = isDissolvedStatus(status)
  return (
    <span
      className={`inline-flex shrink-0 px-1.5 py-0.5 text-xs font-semibold leading-tight rounded-[4px] border ${
        dissolved
          ? 'bg-signal-100 text-signal-900 border-signal-300'
          : 'bg-paper-200 text-ink-700 border-rule'
      }`}
    >
      {formatStatusLabel(status)}
    </span>
  )
}

function entryDisplayMeta(entry: CompanyRoleEntry, variant: AffiliationListVariant) {
  const { affiliation } = entry
  const { from, to } = affiliationPeriod(entry)
  const activeSince = formatActiveSince(from)
  const period = formatAffiliationPeriod(from, to)
  const cvrLine = [
    affiliation.company.cvrNummer ? `CVR ${affiliation.company.cvrNummer}` : null,
    formatCompanyForm(affiliation.company.virksomhedsform),
  ]
    .filter(Boolean)
    .join(' · ')

  let dateLine: string | null = null
  if (variant === 'active' && activeSince) {
    dateLine = `Aktiv siden: ${activeSince}`
  } else if (variant === 'historic' && period) {
    dateLine = period
  }

  return { cvrLine, dateLine }
}

const AffiliationWatchlistControl: React.FC<{
  affiliation: PersonAffiliation
  iconSize?: 'xs' | 'sm' | 'lg'
  revealOnHover?: boolean
}> = ({ affiliation, iconSize = 'sm', revealOnHover = false }) => {
  const cvr = affiliation.company.cvrNummer
  if (!cvr) return null

  return (
    <WatchlistControl
      iconOnly
      iconSize={iconSize}
      revealOnHover={revealOnHover}
      menuPortal
      entityType="company"
      entityId={String(cvr)}
      displayName={affiliation.company.navn || 'Uden navn'}
      countryCode={companyCountryFlagCode(affiliation.company)}
    />
  )
}

const AffiliationCompanyName: React.FC<{
  affiliation: PersonAffiliation
  className?: string
  linkClassName?: string
  showWatchlist?: boolean
}> = ({
  affiliation,
  className = 'text-sm text-ink',
  linkClassName,
  showWatchlist = true,
}) => {
  const cvr = affiliation.company.cvrNummer
  const companyName = affiliation.company.navn || 'Uden navn'
  const flagCode = companyCountryFlagCode(affiliation.company)
  const nameWithFlag = (
    <>
      {companyName}
      {flagCode && <CountryFlagIcon code={flagCode} />}
    </>
  )

  if (cvr) {
    return (
      <EntityProfileLink
        to={`/company/${cvr}`}
        entityType="company"
        entityId={String(cvr)}
        showWatchlist={showWatchlist}
        className={linkClassName ?? `${className} hover:underline decoration-signal underline-offset-2`}
      >
        {nameWithFlag}
      </EntityProfileLink>
    )
  }

  return <span className={className}>{nameWithFlag}</span>
}

const AffiliationEntryList: React.FC<{
  entries: CompanyRoleEntry[]
  variant: AffiliationListVariant
}> = ({ entries, variant }) => (
  <ul
    aria-label={listAriaLabel(variant)}
    className="list-none divide-y divide-rule border-t border-rule"
  >
    {entries.map((entry) => (
      <AffiliationEntryRow key={entryKey(entry)} entry={entry} variant={variant} />
    ))}
  </ul>
)

const AffiliationEntryRow: React.FC<{
  entry: CompanyRoleEntry
  variant: AffiliationListVariant
}> = ({ entry, variant }) => {
  const { affiliation, roles } = entry
  const meta = entryDisplayMeta(entry, variant)

  return (
    <li className="group py-2.5 space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <AffiliationCompanyName affiliation={affiliation} showWatchlist={false} />
        <AffiliationWatchlistControl affiliation={affiliation} revealOnHover />
        {affiliation.company.status && (
          <CompanyStatusBadge status={affiliation.company.status} />
        )}
      </div>
      {meta.cvrLine && (
        <p className="text-xs text-ink-500 tabular-nums">{meta.cvrLine}</p>
      )}
      {meta.dateLine && (
        <p className="text-xs text-ink-500 tabular-nums">{meta.dateLine}</p>
      )}
      <div className="flex flex-wrap items-center gap-1">
        {roles.map((role, i) => (
          <CompactRoleTag key={i} label={roleLabel(role)} />
        ))}
      </div>
    </li>
  )
}

const AffiliationCardGrid: React.FC<{
  entries: CompanyRoleEntry[]
  variant: AffiliationListVariant
}> = ({ entries, variant }) => (
  <ul
    aria-label={listAriaLabel(variant)}
    className="list-none grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5"
  >
    {entries.map((entry) => (
      <AffiliationCard key={entryKey(entry)} entry={entry} variant={variant} />
    ))}
  </ul>
)

const AffiliationCard: React.FC<{
  entry: CompanyRoleEntry
  variant: AffiliationListVariant
}> = ({ entry, variant }) => {
  const { affiliation, roles } = entry
  const cvr = affiliation.company.cvrNummer
  const dissolved = affiliation.company.status
    ? isDissolvedStatus(affiliation.company.status)
    : false
  const meta = entryDisplayMeta(entry, variant)
  const flagCode = companyCountryFlagCode(affiliation.company)

  const companyName = affiliation.company.navn || 'Uden navn'

  return (
    <li className="h-full">
      <div
        className={`group relative h-full rounded-sm border transition-all duration-150 hover:shadow-sm hover:shadow-ink/5 ${
          dissolved && affiliation.company.status
            ? 'border-signal-200 bg-signal-50/30 hover:border-signal hover:bg-signal-50/60'
            : 'border-rule bg-paper hover:border-ink-300'
        }`}
      >
        {cvr && (
          <div className="absolute top-1.5 right-1.5 z-10">
            <AffiliationWatchlistControl affiliation={affiliation} revealOnHover />
          </div>
        )}
        {cvr ? (
          <Link
            to={`/company/${cvr}`}
            className="block h-full p-2.5 pr-8 flex flex-col gap-1"
          >
            <div className="flex flex-wrap items-center gap-1.5 min-w-0">
              <span className="text-sm text-ink leading-snug transition-colors group-hover:underline inline-flex items-baseline gap-1">
                {companyName}
                {flagCode && <CountryFlagIcon code={flagCode} focusable={false} />}
              </span>
              {affiliation.company.status && (
                <CompanyStatusBadge status={affiliation.company.status} />
              )}
            </div>
            {meta.cvrLine && (
              <p className="text-xs text-ink-500 tabular-nums">{meta.cvrLine}</p>
            )}
            {meta.dateLine && (
              <p className="text-xs text-ink-500 tabular-nums">{meta.dateLine}</p>
            )}
            <div className="flex flex-wrap items-center gap-1 mt-auto pt-1.5">
              {roles.map((role, i) => (
                <CompactRoleTag key={i} label={roleLabel(role)} />
              ))}
            </div>
          </Link>
        ) : (
          <div className="block h-full p-2.5 flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-1.5 min-w-0">
              <span className="text-sm text-ink leading-snug inline-flex items-baseline gap-1">
                {companyName}
                {flagCode && <CountryFlagIcon code={flagCode} />}
              </span>
              {affiliation.company.status && (
                <CompanyStatusBadge status={affiliation.company.status} />
              )}
            </div>
            {meta.cvrLine && (
              <p className="text-xs text-ink-500 tabular-nums">{meta.cvrLine}</p>
            )}
            {meta.dateLine && (
              <p className="text-xs text-ink-500 tabular-nums">{meta.dateLine}</p>
            )}
            <div className="flex flex-wrap items-center gap-1 mt-auto pt-1.5">
              {roles.map((role, i) => (
                <CompactRoleTag key={i} label={roleLabel(role)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </li>
  )
}

function entryKey(entry: CompanyRoleEntry): string {
  return entry.affiliation.company.cvrNummer || entry.affiliation.company.navn || 'unknown'
}

export default PersonAffiliationsView
